import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import type { CrmContact, UpsertCrmContactDto } from '@ip/shared-types';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useCrmApi } from '@/services/api';
import { t } from '@/i18n';

interface Props {
  nip: string;
  /** Account the contact belongs to — set on create, never re-sent on edit. */
  accountId: string;
  /** null = create, otherwise edit. */
  contact: CrmContact | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}

interface FormValues {
  firstName: string;
  lastName: string;
  jobTitle: string;
  email: string;
  phone: string;
  isPrimary: boolean;
  notes: string;
}

const EMPTY_FORM: FormValues = {
  firstName: '',
  lastName: '',
  jobTitle: '',
  email: '',
  phone: '',
  isPrimary: false,
  notes: '',
};

export function ContactFormDialog({ nip, accountId, contact, open, onOpenChange, onSaved }: Props) {
  const { createContact, updateContact } = useCrmApi();
  const [form, setForm] = useState<FormValues>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(
      contact
        ? {
            firstName: contact.firstName,
            lastName: contact.lastName ?? '',
            jobTitle: contact.jobTitle ?? '',
            email: contact.email ?? '',
            phone: contact.phone ?? '',
            isPrimary: contact.isPrimary,
            notes: contact.notes ?? '',
          }
        : EMPTY_FORM,
    );
  }, [open, contact]);

  const set = (key: keyof FormValues) => (event: { target: { value: string } }) =>
    setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const dto: UpsertCrmContactDto = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim() || null,
        jobTitle: form.jobTitle.trim() || null,
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        isPrimary: form.isPrimary,
        notes: form.notes.trim() || null,
      };
      if (contact) await updateContact(nip, contact.id, dto);
      else await createContact(nip, { ...dto, accountId });
      toast.success(t('crm.contacts.saved'));
      onSaved();
    } catch {
      toast.error(t('errors.saveFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {contact ? t('crm.contacts.edit') : t('crm.contacts.add')}
          </DialogTitle>
        </DialogHeader>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>{t('crm.contacts.form.firstName')}</Label>
            <Input value={form.firstName} onChange={set('firstName')} />
          </div>
          <div className="space-y-1.5">
            <Label>{t('crm.contacts.form.lastName')}</Label>
            <Input value={form.lastName} onChange={set('lastName')} />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>{t('crm.contacts.form.jobTitle')}</Label>
            <Input value={form.jobTitle} onChange={set('jobTitle')} />
          </div>
          <div className="space-y-1.5">
            <Label>{t('crm.contacts.form.email')}</Label>
            <Input value={form.email} onChange={set('email')} />
          </div>
          <div className="space-y-1.5">
            <Label>{t('crm.contacts.form.phone')}</Label>
            <Input value={form.phone} onChange={set('phone')} />
          </div>
          <div className="flex items-center gap-2 sm:col-span-2">
            <Switch
              id="crm-contact-primary"
              checked={form.isPrimary}
              onCheckedChange={(checked) => setForm((prev) => ({ ...prev, isPrimary: checked }))}
            />
            <Label htmlFor="crm-contact-primary">{t('crm.contacts.form.isPrimary')}</Label>
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>{t('crm.contacts.form.notes')}</Label>
            <Textarea value={form.notes} onChange={set('notes')} rows={3} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={form.firstName.trim() === '' || submitting}>
            {t('crm.contacts.form.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
